import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUtils';
import { formatPrice } from '../utils/currencyUtils';
import './PublicMenu.css';

const API_BASE_URL = 'https://api.hajmetwalli.com';

const PublicMenu = () => {
  const [menus, setMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [settings, setSettings] = useState({
    currency: 'LBP',
    dollarRate: 89000
  });

  useEffect(() => {
    fetchMenus();
    fetchSettings();
  }, []);

  useEffect(() => {
    if (selectedMenu && !loading) {
      fetchMenuData(selectedMenu);
    }
  }, [selectedMenu]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/settings`);
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const fetchMenus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/menus`);
      setMenus(response.data);
      if (response.data.length > 0) {
        setSelectedMenu(response.data[0]._id);
        await fetchMenuData(response.data[0]._id);
      }
    } catch (err) {
      setError('Failed to load menus');
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuData = async (menuId) => {
    try {
      const [categoriesResponse, dishesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/public/categories/${menuId}`),
        axios.get(`${API_BASE_URL}/public/dishes/menu/${menuId}`)
      ]);
      setCategories(categoriesResponse.data);
      setDishes(dishesResponse.data);
    } catch (err) {
      console.error('PublicMenu: Error loading menu data:', err);
      setError('Failed to load menu data');
    }
  };

  const getDishesByCategory = (categoryId) => {
    return dishes.filter(dish => dish.categoryId === categoryId);
  };

  const selectedMenuLabel = menus.find(menu => menu._id === selectedMenu)?.name || 'Signature Menu';

  if (loading) {
    return (
      <div className="public-menu-loading">
        <div className="spinner"></div>
        <p>Loading menu...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-menu-error">
        <h2>Unable to Load Menu</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="public-menu">
      <header className="public-menu-header">
        <div className="header-glow header-glow-left"></div>
        <div className="header-glow header-glow-right"></div>
        <div className="header-content">
          <span className="hero-kicker">Mmetwalli Signature Dining</span>
          <h1 className="restaurant-name">حج متولي</h1>
        </div>
      </header>

      <nav className="menu-selector">
        <div className="menu-selector-shell">
          <div className="menu-tabs">
            {menus.map(menu => (
              <button
                key={menu._id}
                className={`menu-tab ${selectedMenu === menu._id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedMenu(menu._id);
                  fetchMenuData(menu._id);
                }}
              >
                {menu.name}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="menu-content">
        {categories.length === 0 ? (
          <div className="no-categories">
            <h2>No categories available</h2>
            <p>Please check back later for our menu updates.</p>
          </div>
        ) : (
          categories.map(category => {
            const categoryDishes = getDishesByCategory(category._id);
            return (
              <section key={category._id} className="menu-section">
                <h2 className="category-title">{category.name}</h2>
                {categoryDishes.length === 0 ? (
                  <p className="no-dishes">No dishes available in this category yet.</p>
                ) : (
                  <div className="dishes-grid">
                    {categoryDishes.map((dish, index) => (
                      <div key={dish._id} className="dish-card">
                        <div className="dish-card-image">
                          {getImageUrl(dish.imageUrl) ? (
                            <>
                              <img src={getImageUrl(dish.imageUrl)} alt={dish.name} />
                              <button className="image-eye-btn" onClick={() => setLightboxImage(getImageUrl(dish.imageUrl))} aria-label="View full image">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                              </button>
                            </>
                          ) : (
                            <div className="no-dish-image">🍽️</div>
                          )}
                        </div>
                        <div className="dish-card-content">
                          <h3 className="dish-card-name">{dish.name}</h3>
                          {dish.description && (
                            <p className="dish-card-description">{dish.description}</p>
                          )}
                        </div>
                        <div className="dish-card-price">
                          <span className="price-value">{formatPrice(dish.price, settings.currency, settings.dollarRate)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })
        )}
      </main>

      <footer className="public-menu-footer">
        <div className="footer-content">
          <p>📍مفرق بقرزلا</p>
          <p className="phone-number">📞 70 997 709</p>
          <p>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{verticalAlign: 'middle', marginRight: '6px'}}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
            <a 
              href="https://www.instagram.com/metwali_chicken?igsh=a2owdWczODhoeXRs&utm_source=qr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="instagram-handle"
            >
              Instagram
            </a>
          </p>
          <p>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{verticalAlign: 'middle', marginRight: '6px'}}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            <a 
              href="https://www.facebook.com/share/1CJxKBR4wy/?mibextid=wwXIfr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="website-link"
            >
              Facebook
            </a>
          </p>
        </div>
      </footer>

      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <button className="lightbox-close" onClick={() => setLightboxImage(null)} aria-label="Close">✕</button>
          <img src={lightboxImage} alt="Full view" className="lightbox-image" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default PublicMenu;
